/**
 * <hh-video> — a video that loads only when needed.
 * The <video> element lives in a <template> until it's needed, so the
 * browser downloads nothing but the poster image before that.
 *
 * - Tap to play (default): the first tap loads the video and plays it with
 *   sound and controls. Starting one video pauses any other playing <hh-video>.
 * - Autoplay (`data-autoplay`): loads once the video comes near the screen,
 *   plays muted on a loop while it's visible and pauses when it isn't.
 *   Shoppers who prefer reduced motion or have data saver on get tap to play
 *   (the video still plays muted on a loop once tapped).
 */
class HHVideo extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {HTMLVideoElement | null} */
  #video = null;

  connectedCallback() {
    const poster = this.querySelector('.hh-video__poster');

    if (this.hasAttribute('data-autoplay') && HHVideo.#canAutoplay()) {
      this.#observe();
      poster?.addEventListener('click', () => this.#load(true), { once: true });
      return;
    }

    if (this.hasAttribute('data-autoplay')) this.setAttribute('data-tap', '');
    poster?.addEventListener('click', () => this.#load(true), { once: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  static #canAutoplay() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const connection = /** @type {{saveData?: boolean} | undefined} */ (
      /** @type {any} */ (navigator).connection
    );
    return !reduceMotion && !connection?.saveData;
  }

  #observe() {
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (!this.#video) this.#load(false);
            else if (!this.hasAttribute('data-user-paused')) this.#video.play().catch(() => {});
          } else {
            this.#video?.pause();
          }
        }
      },
      { rootMargin: '200px 0px', threshold: 0 }
    );
    this.#observer.observe(this);
  }

  /** @param {boolean} fromTap */
  #load(fromTap) {
    if (this.#video) {
      this.#video.play().catch(() => {});
      return;
    }

    const template = this.querySelector('template');
    const video = template?.content.querySelector('video')?.cloneNode(true);
    if (!(video instanceof HTMLVideoElement)) return;

    video.setAttribute('playsinline', '');
    if (video.loop) {
      // Muted loop: iOS only autoplays when the muted *property* is set as well.
      video.muted = true;
      video.setAttribute('aria-hidden', 'true');
      video.tabIndex = -1;
      this.append(this.#pauseButton(video));
    } else {
      video.addEventListener('play', () => HHVideo.pauseOthers(video));
    }

    this.querySelector('.hh-video__poster')?.remove();
    template?.remove();
    this.append(video);
    this.#video = video;

    if (fromTap && !video.loop) video.focus({ preventScroll: true });
    video.play().catch(() => {
      // Autoplay can be refused (e.g. low-power mode); tap-to-play videos still have their controls.
    });
  }

  /**
   * Looping videos keep moving, so give shoppers a way to stop them.
   * @param {HTMLVideoElement} video
   */
  #pauseButton(video) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'hh-video__toggle';
    const update = () => {
      const paused = video.paused;
      button.setAttribute('aria-label', (paused ? this.dataset.playLabel : this.dataset.pauseLabel) ?? '');
      button.textContent = paused ? '▶' : '❚❚';
    };
    button.addEventListener('click', () => {
      if (video.paused) {
        video.play().catch(() => {});
        this.removeAttribute('data-user-paused');
      } else {
        video.pause();
        this.setAttribute('data-user-paused', '');
      }
    });
    video.addEventListener('play', update);
    video.addEventListener('pause', update);
    update();
    return button;
  }

  /** @param {HTMLVideoElement} current */
  static pauseOthers(current) {
    document.querySelectorAll('hh-video video').forEach((other) => {
      if (other !== current && other instanceof HTMLVideoElement && !other.paused && !other.loop) other.pause();
    });
  }
}

if (!customElements.get('hh-video')) {
  customElements.define('hh-video', HHVideo);
}
